'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Expander from '@pro/components/Widgets/Expander.react';
import NutritionPatternStore from '@stores/NutritionPatternStore';
import Select from '@pro/components/Widgets/Select.react';
import Alert from '@components/Widgets/Alert/Alert.react';
import MealTypesInfo from './MealTypesInfo.react';
import NutritionPatternActions from '@actions/NutritionPatternActions';
import AuthStore from '@stores/AuthStore';
import UserStore from '@stores/UserStore';
import { getConfig } from '@utils/Env';
import { getStartTimeForMeal } from '@utils/Meals';
import { calculateNearestPortionSize, isEatingPatternAllowedForCalories } from '@tables/portions';
import { selectEatingPattern } from '@utils/EatingPatterns';
import './MealDetails.scss';
import './PatientForm.scss';

export default class MealDetails extends Component {
    static propTypes = {
        patient: PropTypes.object.isRequired,
        onChangePatient: PropTypes.func,
        onError: PropTypes.func,
    };
    constructor(props) {
        super(props);

        const { patient } = this.props;
        const { preferences } = patient;

        this.state = {
            loading: false,
            newPatternLoading: false,
            nutritionPatterns: NutritionPatternStore.getNutritionPatterns(),
            availableEatingPatterns: null,
            preferences: {
                ...preferences,
                meal_types: this.setParticipants(preferences.meal_types),
            },
            error: null,
            portionSizeWarning: {
                show: false,
                oldPortion: null,
                newPortion: null,
            },
        };
        this.mealType = {};
        this.setEatingPatternCallId = 0;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Update state when patient props change (e.g., when returning from another page)
        const { patient: currentPatient } = this.props;
        const { patient: nextPatient } = nextProps;
        
        // Check if preferences have changed
        if (nextPatient.preferences !== currentPatient.preferences) {
            this.setState(prevState => ({
                preferences: {
                    ...nextPatient.preferences,
                    meal_types: this.setParticipants(nextPatient.preferences.meal_types || []),
                }
            }));
        }
    }

    componentDidMount = () => {
        const { nutritionPatterns } = this.state;

        if (nutritionPatterns.length) {
            this.setEatingPattern(nutritionPatterns);
        }

        NutritionPatternActions.load();
        NutritionPatternStore.addChangeListener(this.onNutritionPatternStoreChange);
    };

    componentWillUnmount = () => {
        NutritionPatternStore.removeChangeListener(this.onNutritionPatternStoreChange);
    };

    onNutritionPatternStoreChange = () => {
        const nutritionPatterns = NutritionPatternStore.getNutritionPatterns();

        // Update nutritionPatterns in state, but let setEatingPattern manage loading state
        this.setState({ nutritionPatterns });

        if (nutritionPatterns.length) {
            this.setEatingPattern(nutritionPatterns);
        }
    };

    setEatingPattern = async (nutritionPatterns) => {
        const { preferences } = this.state;
        const { patient } = this.props;
        const { conditions } = patient;

        if (!(conditions && conditions[0])) {
            return;
        }

        const pattern = nutritionPatterns.find((pattern) => pattern.uuid === conditions[0].uuid);

        if (!pattern) {
            return;
        }

        // Guard against concurrent calls - increment and capture call ID
        this.setEatingPatternCallId++;
        const callId = this.setEatingPatternCallId;

        this.setState({ loading: true });

        try {
            // Resolve pattern and calculate portion (works for both initial load and refresh)
            const { newPreferences, mealTypesWithParticipants, portionInfo } =
                await this.resolvePatternWithPortion(preferences.eating_pattern_name, nutritionPatterns);

            // Check if this call is still current - if not, abort
            if (callId !== this.setEatingPatternCallId) {
                return;
            }

            const allEatingPatterns = newPreferences.available_eating_patterns;

            // Filter out eating patterns with not_allowed brackets for current calorie level
            const filteredEatingPatterns = allEatingPatterns.filter(eatingPattern =>
                isEatingPatternAllowedForCalories(eatingPattern, patient.target_energy_kcal)
            );

            // Use utility function to handle disabled patterns
            const {
                selectedPatternName,
                selectedMealTypes,
                availablePatterns: availableEatingPatterns,
                isCurrentPatternDisabled
            } = selectEatingPattern({
                allPatterns: filteredEatingPatterns,
                currentPatternName: preferences.eating_pattern_name,
                fallbackPatternName: newPreferences.eating_pattern_name,
                includeDisabledCurrent: true,
                currentMealTypes: preferences.meal_types,
            });

            if (preferences.eating_pattern_name) {
                // Patient already has an eating pattern selected - just show warning if portion changed
                this.setState({
                    availableEatingPatterns,
                    isCurrentPatternDisabled,
                    loading: false,
                    portionSizeWarning: {
                        show: portionInfo?.portionChanged || false,
                        oldPortion: portionInfo?.baselinePortion,
                        newPortion: portionInfo?.newPortion,
                    },
                });
                return;
            }

            // Patient doesn't have an eating pattern yet - set up preferences with selected pattern
            const finalMealTypes = this.setParticipants(selectedMealTypes);

            // Preserve existing avoidances
            (patient.preferences.avoidances || []).forEach((avoidance) => {
                if (!newPreferences.avoidances.includes(avoidance)) {
                    newPreferences.avoidances.push(avoidance);
                }
            });

            const updatedPreferences = {
                ...preferences,
                ...newPreferences,
                eating_pattern_name: selectedPatternName,
                meal_types: finalMealTypes,
            };

            this.setState({
                preferences: updatedPreferences,
                availableEatingPatterns,
                isCurrentPatternDisabled,
                loading: false
            });
            
            // Immediately sync changes back to parent
            this.syncToParent(updatedPreferences);
        } catch (error) {
            // Only update state if this call is still current
            if (callId !== this.setEatingPatternCallId) {
                return;
            }
            this.setState({ loading: false });
            if (this.props.onError) {
                this.props.onError(error.message || 'Unable to build a meal plan that fits these nutrition parameters');
            }
            return;
        }
    };

    /**
     * Shared logic for resolving a pattern and calculating portion size
     * Returns the resolved preferences with participants and portion calculation
     */
    resolvePatternWithPortion = async (eatingPatternName, nutritionPatterns) => {
        const { preferences } = this.state;
        const { patient } = this.props;
        const { gender, birthdate, weight_kg, target_energy_kcal, conditions } = patient;

        const pattern = nutritionPatterns.find((p) => p.uuid === conditions[0].uuid);
        const query = {
            gender,
            birthdate,
            weight_kg,
            target_energy_kcal,
            diets: patient.preferences?.diets,
            eating_pattern: eatingPatternName,
            calculate_implicit: true,
        };

        const { preferences: newPreferences } = await AuthStore.fetch({
            url: getConfig('users_api') + pattern.links.resolve.href,
            query
        });

        // Calculate portion size using brackets if available
        // Brackets are on the eating pattern in available_eating_patterns, not on preferences
        const availablePatterns = newPreferences[0].available_eating_patterns || [];
        const selectedPattern = availablePatterns.find(p => p.name === eatingPatternName);
        const brackets = selectedPattern?.portion_size_brackets || null;

        const { standard_calories = 2000, inhibit_deadzone = false } = UserStore.getUser()?.features?.energy_estimator || {};
        const portion_resolution = patient.portion_resolution || 0.25;
        const baselinePortion = patient.portion || 1;

        const bracketPortion = calculateNearestPortionSize(
            target_energy_kcal,
            portion_resolution,
            standard_calories,
            inhibit_deadzone,
            brackets
        );

        const newPortion = bracketPortion || 1;
        const portionChanged = newPortion !== baselinePortion;
        const mealTypesWithParticipants = this.setParticipants(newPreferences[0].meal_types);

        return {
            newPreferences: newPreferences[0],
            mealTypesWithParticipants,
            portionInfo: {
                portionChanged,
                baselinePortion,
                newPortion,
            },
        };
    };

    onSelectEatingPattern = async (name) => {
        const { availableEatingPatterns, nutritionPatterns, preferences } = this.state;
        const eatingPattern = availableEatingPatterns.find((pattern) => name === pattern.name);

        this.setState({
            newPatternLoading: true,
            preferences: {
                ...preferences,
                eating_pattern_name: eatingPattern.name,
                meal_types: eatingPattern.meal_types,
            },
        });

        try {
            const { newPreferences, mealTypesWithParticipants, portionInfo } =
                await this.resolvePatternWithPortion(eatingPattern.name, nutritionPatterns);

            const updatedPreferences = {
                ...preferences,
                eating_pattern_name: newPreferences.eating_pattern_name,
                meal_types: mealTypesWithParticipants,
            };

            this.setState({
                preferences: updatedPreferences,
                newPatternLoading: false,
                dirty: true,
                portionSizeWarning: {
                    show: portionInfo.portionChanged,
                    oldPortion: portionInfo.baselinePortion,
                    newPortion: portionInfo.newPortion,
                },
            });
            
            // Immediately sync changes back to parent
            this.syncToParent(updatedPreferences);
        } catch (error) {
            this.setState({
                newPatternLoading: false,
                error: 'Unable to load eating pattern.'
            });
        }
    };

    setParticipants = (mealTypes) => {
        const { patient } = this.props;
        const { family = [] } = patient;
        const defaultParticipants = ['USER'].concat(family.map((member) => member.uuid));
        const updatedMealTypes = mealTypes.map((mealType) => ({
            ...mealType,
            participants: mealType.participants || defaultParticipants,
        }));
        return updatedMealTypes;
    };

    isDirty = () => {
        return this.state.dirty;
    };

    validate = () => {
        const { preferences } = this.state;
        const { meal_types } = preferences;
        const maxCostMinimumValue = {
            Breakfast: 3.25,
            Lunch: 5,
            Snack: 3,
            Dinner: 5,
        };

        let error;
        const mealTypeNames = [];
        for (let mealType of meal_types) {
            if (mealTypeNames.includes(mealType.name)) {
                error = {
                    name: `${mealType.name}-meal-type`,
                    message: `Meal name "${mealType.name}" is already used. Each meal must have a unique name.`,
                };
                break;
            }

            if (mealType.max_cost && mealType.max_cost < maxCostMinimumValue[mealType.main_dish]) {
                error = {
                    name: `${mealType.name}-max-cost`,
                    message: `Max cost must be greater than ${maxCostMinimumValue[mealType.main_dish]} for ${mealType.name}`,
                };
                break;
            }

            mealTypeNames.push(mealType.name);
        }

        if (error) {
            this.setState({ error: error.name });
            return error.message;
        }

        this.setState({ error: null });
        return true;
    };

    mutate = (patient) => {
        const { preferences } = this.state;

        patient.preferences = { ...patient.preferences, ...preferences };

        if (!patient.completed.includes('mealDetails')) {
            patient.completed.push('mealDetails');
        }

        return patient;
    };

    onChangeMealType = (mealType, index) => {
        const { preferences } = this.state;
        const mealTypes = [...preferences.meal_types];
        mealTypes[index] = mealType;
        const updatedPreferences = { ...preferences, meal_types: mealTypes };
        this.setState({ preferences: updatedPreferences, dirty: true });
        
        // Immediately sync changes back to parent
        this.syncToParent(updatedPreferences);
    };

    syncToParent = (updatedPreferences) => {
        const { patient, onChangePatient } = this.props;
        if (onChangePatient) {
            const updatedPatient = {
                ...patient,
                preferences: {
                    ...patient.preferences,
                    ...updatedPreferences
                }
            };
            onChangePatient(updatedPatient);
        }
    };

    getSubLabel = (mealTypes = []) => {
        const mealTypeNames = mealTypes
            .map((mealType) => {
                // Convert to title case
                return mealType.name
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            })
            .join(', ');

        const label = (
            <span>
                {mealTypes.length} MEALS PER DAY<br/><span className="meal-type-names">{mealTypeNames}</span>
            </span>
        );

        return label;
    }

    render() {
        const { availableEatingPatterns, loading, newPatternLoading, preferences, error, portionSizeWarning } = this.state;
        const { patient } = this.props;

        if (!availableEatingPatterns || !preferences || loading) {
            return (
                <div className="meal-details">
                    <div className="meal-types-loader">
                        <i className="icon-spinner2" />
                    </div>
                </div>
            );
        }

        const mealTypes = preferences.meal_types;

        const eatingPatternOptions = availableEatingPatterns.map((pattern) => ({
            value: pattern.name,
            label: this.getSubLabel(pattern.meal_types),
        }));

        // Ensure the selected value is valid
        // If current eating_pattern_name is not in the options, use the first available one
        let selectedEatingPattern = preferences.eating_pattern_name;
        const isValidSelection = eatingPatternOptions.some((option) => option.value === selectedEatingPattern);

        if (!isValidSelection && eatingPatternOptions.length > 0) {
            selectedEatingPattern = eatingPatternOptions[0].value;
        }

        const maxTimeLabels = {
            900: 'Under 15 Minutes',
            1800: 'Under 30 Minutes',
            2700: 'Under 45 Minutes',
            3600: 'Under 1 Hour',
        };

        return (
            <div className="meal-details patient-form">
                <div className='with-label eating-pattern'>
                    <label>Eating Pattern:</label>
                    <Select
                        value={selectedEatingPattern}
                        options={eatingPatternOptions}
                        onChange={(value) => this.onSelectEatingPattern(value)}
                    />
                </div>
                {portionSizeWarning.show && (
                    <Alert
                        type="warning"
                        dismissible={true}
                        onDismiss={() => this.setState({
                            portionSizeWarning: {
                                ...portionSizeWarning,
                                show: false,
                            }
                        })}>
                        Based on the selected eating pattern, we recommend a portion size of {portionSizeWarning.newPortion} instead of {portionSizeWarning.oldPortion}.
                    </Alert>
                )}
                {newPatternLoading ? (
                    <div className="meal-types-loader">
                        <i className="icon-spinner2" />
                    </div>
                ) : null}
                {!newPatternLoading
                    ? mealTypes.map((mealType, index) => {
                          const startTime = getStartTimeForMeal(mealType.begin);
                          const endTime = getStartTimeForMeal(mealType.end);
                          const participants = [{ uuid: 'USER', name: patient.first_name }]
                              .concat(patient.family || [])
                              .filter((member) => (mealType.participants || []).includes(member.uuid));
                          const participantNames = participants.map((participant) => participant.name).join(', ');
                          return (
                              <Expander
                                  key={index}
                                  button={
                                      <div className="meal-type-expand">
                                          <div className="meal-type-label">
                                              <div className="meal-type-name">{mealType.name}</div>
                                              <div className="meal-type-range">
                                                  {startTime} - {endTime}
                                              </div>
                                          </div>
                                          <div className="meal-type-details">
                                              {(patient.family || []).length ? (
                                                  <span>
                                                      {participants.length > 0 ? (
                                                          <span>{participantNames}</span>
                                                      ) : (
                                                          <span>No participants</span>
                                                      )}
                                                  </span>
                                              ) : null}

                                              <span>
                                                  {mealType.max_cost ? (
                                                      <span>Max ${mealType.max_cost}/serving</span>
                                                  ) : null}
                                                  {mealType.max_time && maxTimeLabels[mealType.max_time] ? (
                                                      <span>{maxTimeLabels[mealType.max_time]}</span>
                                                  ) : null}
                                                  {mealType.keywords ? <span>Prefer: {mealType.keywords}</span> : null}
                                              </span>
                                          </div>
                                      </div>
                                  }
                                  isCollapsible={true}
                              >
                                  <MealTypesInfo
                                      key={`${index}-${preferences.eating_pattern_name}`}
                                      mealType={mealType}
                                      patient={patient}
                                      onChange={(mealType) => this.onChangeMealType(mealType, index)}
                                      error={error}
                                  />
                              </Expander>
                          );
                      })
                    : null}
            </div>
        );
    }
}
